// lib/observability/sentry-scrub.ts — shared Sentry PII scrubber.
//
// Audit 2026-07-11 (LOW, sentry.server.config.ts): the Sentry inits
// relied entirely on the SDK default plus the assumption that no code
// path ever interpolates PII (email / token / secret) into an error
// message. That is not defence-in-depth: one `throw new Error(\`no user
// for ${email}\`)` ships the address straight to Sentry.
//
// This module provides a single `scrubSentryEvent` `beforeSend` hook
// that every runtime init wires in, plus the `SEND_DEFAULT_PII` flag
// (always false) for explicitness. It does two things:
//   1. Redacts email- and token/secret-shaped substrings from every
//      free-text field of an outgoing event (message, exception
//      values, breadcrumb messages, request cookies/query).
//   2. Drops the value of any key whose name matches a known-sensitive
//      list (authorization, cookie, password, *_token, api_key, …) in
//      extra / contexts / tags / request maps.
//
// It walks a bounded depth so a pathological nested payload cannot turn
// the hook into an O(n^2) hot path on the error path.

import type { ErrorEvent } from '@sentry/nextjs';

/** Passed to every `Sentry.init` for explicitness; never send SDK-default PII. */
export const SEND_DEFAULT_PII = false as const;

const REDACTED = '[redacted]';
const REDACTED_EMAIL = '[redacted-email]';
const REDACTED_TOKEN = '[redacted-token]';

// Bounded recursion depth for nested extra/context objects — deep
// enough for real payloads, shallow enough to stay cheap on the error
// path.
const MAX_DEPTH = 6;

// Key names whose *value* is dropped wholesale regardless of shape.
// Lower-cased; matched as a case-insensitive substring so that
// `X-Api-Key`, `stripe_api_key`, `userPassword` all hit.
const SENSITIVE_KEY_FRAGMENTS: readonly string[] = [
  'authorization',
  'cookie',
  'password',
  'passwd',
  'secret',
  'token', // access_token, refresh_token, csrf_token, …
  'api_key',
  'apikey',
  'api-key',
  'client_secret',
  'private_key',
  'session',
  'jwt',
  'dsn',
];

// Substring patterns redacted inside any free-text string.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// `Bearer <token>` / `Basic <creds>` auth headers.
const AUTH_HEADER_RE = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/-]+=*/gi;
// JWTs: three base64url segments — always start with `eyJ`.
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
// Common provider secret prefixes (Stripe, Slack, GitHub, OpenAI, …)
// followed by a run of key-ish chars.
const SECRET_PREFIX_RE = /\b(sk|pk|rk|whsec|xox[baprs]|ghp|gho|ghs|github_pat|AIza|AKIA)[-_][A-Za-z0-9_-]{8,}/gi;
// `token=...`, `password: ...`, `secret=...` style key/value pairs in a
// flat string (query strings, log lines) — redact the value only.
const KV_SECRET_RE =
  /\b(password|passwd|secret|token|api[_-]?key|access[_-]?token|refresh[_-]?token)([=:]\s*)([^\s&"']+)/gi;

/**
 * Redact PII/secret-shaped substrings from a single free-text value.
 * Pure — returns a new string, never mutates.
 */
export function redactString(input: string): string {
  if (!input) return input;
  return input
    .replace(AUTH_HEADER_RE, (_m, scheme: string) => `${scheme} ${REDACTED_TOKEN}`)
    .replace(JWT_RE, REDACTED_TOKEN)
    .replace(SECRET_PREFIX_RE, REDACTED_TOKEN)
    .replace(KV_SECRET_RE, (_m, key: string, sep: string) => `${key}${sep}${REDACTED_TOKEN}`)
    .replace(EMAIL_RE, REDACTED_EMAIL);
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_FRAGMENTS.some((frag) => lower.includes(frag));
}

/**
 * Recursively scrub an arbitrary value: redact strings, drop the value
 * of sensitive keys, recurse into arrays/objects up to MAX_DEPTH.
 * Returns a new value; does not mutate the input.
 */
function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return redactString(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return value;

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }

  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = isSensitiveKey(key) ? REDACTED : scrubValue(val, depth + 1);
  }
  return out;
}

/**
 * Sentry `beforeSend` hook. Scrubs the free-text and structured fields
 * of an outgoing error event in place-safe fashion (constructs new
 * sub-objects, never returns null so events are still delivered).
 *
 * Wired identically into sentry.server.config.ts, sentry.edge.config.ts
 * and instrumentation-client.ts so the guarantee holds on every runtime.
 */
export function scrubSentryEvent(event: ErrorEvent): ErrorEvent {
  if (event.message) {
    event.message = redactString(event.message);
  }
  if (event.logentry?.message) {
    event.logentry.message = redactString(event.logentry.message);
  }

  const exceptionValues = event.exception?.values;
  if (exceptionValues) {
    for (const ex of exceptionValues) {
      if (ex.value) ex.value = redactString(ex.value);
    }
  }

  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.message) crumb.message = redactString(crumb.message);
      if (crumb.data) crumb.data = scrubValue(crumb.data, 0) as typeof crumb.data;
    }
  }

  if (event.extra) {
    event.extra = scrubValue(event.extra, 0) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = scrubValue(event.contexts, 0) as typeof event.contexts;
  }
  if (event.tags) {
    event.tags = scrubValue(event.tags, 0) as typeof event.tags;
  }

  const request = event.request;
  if (request) {
    // Auth headers / cookies are the highest-risk request fields.
    if (request.cookies) request.cookies = REDACTED as unknown as typeof request.cookies;
    if (request.headers) request.headers = scrubValue(request.headers, 0) as typeof request.headers;
    if (typeof request.query_string === 'string') {
      request.query_string = redactString(request.query_string);
    }
    if (request.data) request.data = scrubValue(request.data, 0) as typeof request.data;
  }

  return event;
}
